import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  IconButton,
  Button,
  Typography,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useDialog } from '@/components/dialogs/useDialog';
import type { Account, NostroAccount, NostroOperationResult, PaymentIntermediariesRecord } from '@/types/accounts';
import { NostroOperationType } from '@/types/accounts';
import type { M3ApiResponse } from '@/types/api';
import { TopupWithdrawDialog } from './TopupWithdrawDialog';

export interface AccountDetailDialogProps {
  nostroAccount: NostroAccount;
  paymentIntermediaries: PaymentIntermediariesRecord[];
  onClose: (result: M3ApiResponse<NostroOperationResult> | null) => void;
}

function formatXAF(value: number): string {
  return `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0 }).format(value)} XAF`;
}

function creditColor(value: number): string {
  if (value > 0) return 'success.main';
  if (value < 0) return 'error.main';
  return 'warning.main';
}

const ROWS_PER_PAGE = 10;

export function AccountDetailDialog({
  nostroAccount,
  paymentIntermediaries,
  onClose,
}: AccountDetailDialogProps) {
  const { t } = useTranslation();
  const { open: openDialog } = useDialog();
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const term = filter.toLowerCase();
    if (!term) return nostroAccount.accounts;
    return nostroAccount.accounts.filter(
      (a) =>
        a.accountNumber.toLowerCase().includes(term) ||
        String(a.credit).includes(term) ||
        a.providerAccountType.toLowerCase().includes(term),
    );
  }, [nostroAccount.accounts, filter]);

  const paged = filtered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  const handleOperation = async (account: Account, operation: NostroOperationType) => {
    const result = await openDialog<M3ApiResponse<NostroOperationResult> | null>((close) => (
      <TopupWithdrawDialog
        operation={operation}
        account={account}
        paymentIntermediaries={paymentIntermediaries}
        onClose={close}
      />
    ));

    if (result !== null) {
      if (result.code === 0) {
        onClose(result);
      } else if (result.code === 103) {
        const uniqueRef =
          result.result?.transfer?.uniqueReference ?? result.result?.uniqueReference;
        await Swal.fire({
          icon: 'warning',
          title: t('messages.operation-pending-tile', { defaultValue: 'Operation pending' }),
          text: t('messages.operation-pending', {
            defaultValue: 'The operation is pending approval.',
          }),
          footer: uniqueRef
            ? `Ref. <strong><span style="padding-left:10px">${uniqueRef}</span></strong>`
            : undefined,
        });
        onClose(null);
      }
    }
  };

  const showTopupWithdraw = (account: Account) =>
    account.accountNumber.toLowerCase() !== 'cr' && account.provider.id !== 0;

  const showAdjust = (account: Account) => account.provider.id !== 0;

  return (
    <Dialog open onClose={() => onClose(null)} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {nostroAccount.accountName}
        <IconButton onClick={() => onClose(null)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(0);
            }}
            placeholder={t('pages.accounts.dialogDetails.filter', { defaultValue: 'Filter' })}
            sx={{ minWidth: 260 }}
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t('pages.accounts.dialogDetails.table.header.accountNumber', {
                    defaultValue: 'Account number',
                  })}
                </TableCell>
                <TableCell align="right">
                  {t('pages.accounts.dialogDetails.table.header.credit', {
                    defaultValue: 'Credit',
                  })}
                </TableCell>
                <TableCell>
                  {t('pages.accounts.dialogDetails.table.header.providerAccountType', {
                    defaultValue: 'Provider account type',
                  })}
                </TableCell>
                <TableCell align="center">
                  {t('pages.accounts.dialogDetails.table.header.status', {
                    defaultValue: 'Status',
                  })}
                </TableCell>
                <TableCell align="center">
                  {t('pages.accounts.dialogDetails.table.header.actions', {
                    defaultValue: 'Actions',
                  })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((account) => (
                <TableRow key={account.id} hover>
                  <TableCell>{account.accountNumber}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      component="span"
                      color={creditColor(account.credit)}
                    >
                      {formatXAF(account.credit)}
                    </Typography>
                  </TableCell>
                  <TableCell>{account.providerAccountType}</TableCell>
                  <TableCell align="center">{account.accountStatus}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      {showTopupWithdraw(account) && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => handleOperation(account, NostroOperationType.ADD_FUNDS)}
                        >
                          {t('pages.accounts.dialogDetails.actions.topup', {
                            defaultValue: 'Top up',
                          })}
                        </Button>
                      )}
                      {showTopupWithdraw(account) && (
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          onClick={() =>
                            handleOperation(account, NostroOperationType.WITHDRAW_FUNDS)
                          }
                        >
                          {t('pages.accounts.dialogDetails.actions.withdraw', {
                            defaultValue: 'Withdraw',
                          })}
                        </Button>
                      )}
                      {showAdjust(account) && (
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          onClick={() => handleOperation(account, NostroOperationType.ADJUST)}
                        >
                          {t('pages.accounts.dialogDetails.actions.adjust', {
                            defaultValue: 'Adjust',
                          })}
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {paged.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {t('messages.no-data', { defaultValue: 'No data' })}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          labelDisplayedRows={({ from, to, count }) =>
            t('pages.accounts.dialogDetails.table.pagination.range', {
              defaultValue: `${from}-${to} of ${count}`,
              from,
              to,
              count,
            })
          }
        />
      </DialogContent>
    </Dialog>
  );
}
